"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, FileCode2, Hammer, Wrench, ArrowRight, CircleCheck as CheckCircle2, Layers } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const steps = [{
  number: "01",
  phase: "Consultation",
  description: "We visit your home to listen to your goals, assess the space, and discuss your vision for the project.",
  icon: Compass,
  deliverables: "Site visit · Needs analysis · Goal setting"
}, {
  number: "02",
  phase: "Scope & estimate",
  description: "We provide a clear, written scope of work and a detailed estimate so you know exactly what to expect.",
  icon: FileCode2,
  deliverables: "Written scope · Project estimate · Timeline plan"
}, {
  number: "03",
  phase: "Permits & planning",
  description: "We handle all necessary plans and permits while you finalize your material and finish selections.",
  icon: Hammer,
  deliverables: "Permit filing · Material selection · Final design"
}, {
  number: "04",
  phase: "Build & walkthrough",
  description: "A scheduled build with regular progress updates, ending with a final walkthrough to ensure quality.",
  icon: Wrench,
  deliverables: "Quality build · Regular updates · Final review"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 18
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function Workflow() {
  const shouldReduceMotion = useReducedMotion();
  return <section data-section-id="195983" id="howitworks" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 pb-12 sm:pb-16 border-b border-border">
          <div className="max-w-2xl space-y-4">
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="bg-accent text-accent-foreground border-accent-foreground/20 text-xs font-semibold uppercase tracking-wider py-1 px-3 rounded-full">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
                Our simple process
              </Badge>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground leading-[1.12]">
              How it works
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
              A clear, organized process with a written scope, a schedule and regular updates.
            </p>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <Button asChild size="lg" className="rounded-sm font-semibold tracking-wide">
              <Link to="/#request-estimate" className="inline-flex items-center gap-2">
                Request an estimate
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* 4-Step Process Grid */}
        <motion.div variants={shouldReduceMotion ? undefined : containerVariants} initial={shouldReduceMotion ? undefined : "hidden"} whileInView={shouldReduceMotion ? undefined : "visible"} viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-12 sm:pt-16">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <motion.div key={step.number} data-index={i} variants={shouldReduceMotion ? undefined : itemVariants} className="group relative flex flex-col justify-between bg-card text-card-foreground border border-border rounded-md p-6 sm:p-7 shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200 hover:shadow-md">
                {/* Top Bar with Number & Amber Pip */}
                <div>
                  <div className="flex items-center justify-between pb-6 border-b border-border/60">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-3.5 bg-primary rounded-xs" />
                      <span className="font-mono text-sm font-bold tracking-wider text-muted-foreground group-hover:text-foreground transition-colors">
                        STEP {step.number}
                      </span>
                    </div>

                    <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                      <Icon className="size-5" />
                    </div>
                  </div>

                  {/* Step Title & Description */}
                  <div className="pt-6 space-y-3">
                    <h3 className="text-lg sm:text-xl font-default font-bold text-foreground group-hover:text-foreground">
                      {step.phase}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Bottom Deliverables Tag */}
                <div className="pt-6 mt-6 border-t border-border/60">
                  <div className="flex items-start gap-2 text-xs font-mono text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                    <span className="leading-tight">{step.deliverables}</span>
                  </div>
                </div>

                {/* Subtle Hover Hairline Indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left rounded-b-md" />
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Assurance Strip */}
        <div className="mt-12 sm:mt-16 bg-muted/50 border border-border rounded-md p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-card border border-border flex items-center justify-center text-primary shrink-0">
              <Layers className="size-6" />
            </div>
            <div>
              <h4 className="text-base font-default font-semibold text-foreground">
                Professional project management
              </h4>
              <p className="text-sm text-muted-foreground">
                You always know who your contact is and what happens next.
              </p>
            </div>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" size="sm" className="font-semibold text-xs uppercase tracking-wider">
              <Link to="/ProjectsPage">View our work</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}